<template>
    <div class="base-input" :class="frameClass()" :style="frameStyle()">
        <!-- The frame draws the border and focus ring so prefix and suffix content share the
             field's box; the input inside is borderless. A caller's class and style dress the frame,
             and every other attr (placeholder, aria-label, name, disabled, listeners) reaches the input. -->
        <span v-if="$slots.prefix" class="affix">
            <slot name="prefix"></slot>
        </span>
        <!-- Labelled by a wrapping FormField via for/id, or by an aria-label callers pass
             bare via fallthrough attrs, which the a11y rule can't statically see. -->
        <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
        <input v-bind="inputAttrs()" v-model="model" :type="type" class="control fs-mask" />
        <span v-if="$slots.suffix" class="affix">
            <slot name="suffix"></slot>
        </span>
    </div>
</template>

<script lang="ts">
interface BaseInputProps extends /* @vue-ignore */ InputHTMLAttributes {
    /**
     * Single-line text-like input types only; block-level or non-text types get their own component.
     * A search field is `type="search"` with the search glyph in the prefix slot.
     */
    type?: "text" | "email" | "tel" | "url" | "password" | "search";
    /** `compact` is the narrow header and toolbar field; `default` fills its container. */
    size?: "default" | "compact";
}
</script>

<script setup lang="ts">
import type { HTMLAttributes, InputHTMLAttributes, StyleValue } from "vue";
import { useAttrs } from "vue";

import { useFormField } from "./formFieldContext";

defineOptions({ inheritAttrs: false });

defineSlots<{
    /** Leading content inside the field, such as the search glyph. Mark icons aria-hidden. */
    prefix?(): unknown;
    /** Trailing content inside the field, such as an action button. Give any button an aria-label. */
    suffix?(): unknown;
}>();

const { type = "text", size = "default" } = defineProps<BaseInputProps>();

const model = defineModel<string>({ default: "" });

// Wraps itself into a surrounding FormField when present; used bare it stays a plain
// styled input (placeholder, name, autocomplete, disabled, ... arrive via fallthrough).
const field = useFormField();

// These helpers run in the template, so every render reads the current attrs directly and a
// caller's :disabled or FormField's error reaches the input and frame without a cached copy.
const attrs = useAttrs();

// Caller attrs win over the FormField wiring, so an explicit id or aria-describedby still applies.
function inputAttrs(): Record<string, unknown> {
    const { class: _class, style: _style, ...rest } = attrs;
    return { ...field?.controlAttrs.value, ...rest };
}

// The frame shows the input's disabled and error states, since it is the element that draws them.
function frameClass(): HTMLAttributes["class"] {
    const input = inputAttrs();
    const isDisabled = input.disabled !== undefined && input.disabled !== false;
    const isInvalid = String(input["aria-invalid"]) === "true";
    return [sizeClasses[size], { "is-disabled": isDisabled, "is-invalid": isInvalid }, attrs.class as HTMLAttributes["class"]];
}

function frameStyle(): StyleValue {
    return attrs.style as StyleValue;
}

const sizeClasses: Record<NonNullable<BaseInputProps["size"]>, string> = {
    default: "",
    compact: "is-compact",
};
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .base-input {
        @apply flex w-full items-center gap-2.5 rounded-md border border-control-border bg-control-bg p-2.5 transition-colors focus-field-within;
    }

    .base-input.is-compact {
        @apply w-64 py-2;
    }

    .base-input.is-disabled {
        @apply bg-control-disabled-bg;
    }

    .base-input.is-invalid {
        @apply border-status-error;
    }

    .base-input .control {
        @apply min-w-0 flex-1 border-none bg-transparent p-0 outline-none body-small text-control-text-strong placeholder:text-control-placeholder;
    }

    /* The field draws its own affordances; drop the browser's clear and decoration for type="search". */
    .base-input .control::-webkit-search-cancel-button,
    .base-input .control::-webkit-search-decoration {
        @apply appearance-none;
    }

    .base-input .affix {
        @apply flex shrink-0 items-center text-base/none text-control-text;
    }
}
</style>
